import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  Eye,
  FileText,
  Loader2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { apiFetch, apiFetchBlob, getToken } from "@/api";
import { getPyqSubjects } from "@/data/curriculum";
import UserAvatar from "@/components/UserAvatar";
import PageBlobs from "@/components/PageBlobs";
import PdfViewer from "@/components/PdfViewer";

/* PYQ Paper Library — real uploaded board-exam PDFs (never AI-generated).
   Students pick Board / Class / Subject, filter by year and exam type, then
   view or download the original paper. Free for every logged-in student. */

const pill = (active) =>
  `rounded-full border-2 px-4 py-2 text-xs font-bold transition-[border-color,background-color] duration-200 ${
    active
      ? "border-[#16A34A] bg-[#16A34A]/10 text-[#17201A]"
      : "border-[#17201A]/10 bg-white text-[#17201A]/60 hover:border-[#16A34A]/40"
  }`;

const fmtSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const PyqLibrary = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [board, setBoard] = useState(null); // "CBSE" | "ICSE"
  const [klass, setKlass] = useState(null); // "10" | "12"
  const [subject, setSubject] = useState("");
  const [papers, setPapers] = useState(null);
  const [yearFilter, setYearFilter] = useState("all");
  const [viewer, setViewer] = useState(null); // { name, url }
  const [viewerLoading, setViewerLoading] = useState(false);

  useEffect(() => {
    if (!getToken()) {
      navigate("/login");
      return;
    }
    apiFetch("/me")
      .then((u) => {
        setMe(u);
        setBoard(u.board === "CBSE" ? "CBSE" : "ICSE");
        setKlass(["10", "12"].includes(String(u.klass)) ? String(u.klass) : "10");
      })
      .catch(() => navigate("/login"));
  }, [navigate]);

  const subjects = useMemo(
    () => (board && klass ? getPyqSubjects(board, klass) : []),
    [board, klass]
  );

  useEffect(() => {
    if (subjects.length && !subjects.includes(subject)) setSubject(subjects[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjects]);

  useEffect(() => {
    if (!board || !klass || !subject) return;
    setPapers(null);
    setYearFilter("all");
    apiFetch(
      `/pyq/papers?board=${board}&klass=${klass}&subject=${encodeURIComponent(subject)}`
    )
      .then((d) => setPapers(d.papers))
      .catch((e) => {
        toast.error(e.message);
        setPapers([]);
      });
  }, [board, klass, subject]);

  const years = useMemo(
    () =>
      [...new Set((papers || []).map((p) => p.year).filter(Boolean))].sort(
        (a, b) => b - a
      ),
    [papers]
  );
  // Regular + Compartment papers appear together — no separate type step
  const shown = (papers || []).filter(
    (p) => yearFilter === "all" || p.year === Number(yearFilter)
  );

  const openViewer = async (paper) => {
    setViewerLoading(true);
    try {
      const blob = await apiFetchBlob(`/pyq/papers/${paper.id}/file`);
      setViewer({
        name: paper.filename,
        url: URL.createObjectURL(blob),
        blob,
        paper,
        // Native iframe PDF plugins are broken on mobile (page 1 only,
        // no scroll) — touch devices get the canvas renderer instead.
        touch: window.matchMedia("(pointer: coarse)").matches,
      });
    } catch (e) {
      toast.error(e.message);
    }
    setViewerLoading(false);
  };

  const closeViewer = () => {
    if (viewer?.url) URL.revokeObjectURL(viewer.url);
    setViewer(null);
  };

  const download = async (paper) => {
    try {
      const blob = await apiFetchBlob(`/pyq/papers/${paper.id}/file?download=1`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = paper.filename || "paper.pdf";
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (!me) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F8FAF7]">
        <Loader2 className="h-7 w-7 animate-spin text-[#16A34A]" />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#F8FAF7]" data-testid="pyq-page">
      <PageBlobs />
      <header className="relative mx-auto flex max-w-3xl items-center justify-between px-6 py-6">
        <button
          data-testid="pyq-back-btn"
          onClick={() => navigate("/dashboard")}
          className="inline-flex items-center gap-1.5 rounded-full border border-[#E3ECE6] bg-white px-4 py-2 text-xs font-bold text-[#17201A] transition-colors duration-200 hover:border-[#FACC15]"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Dashboard
        </button>
        <UserAvatar />
      </header>

      <main className="relative mx-auto max-w-2xl px-6 pb-20 pt-4">
        <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#16A34A]">
          Previous Year Papers
        </p>
        <h1 className="mt-2 font-display text-3xl font-extrabold text-[#17201A]">
          PYQ Paper Library
        </h1>
        <p className="mt-1.5 text-sm font-semibold text-[#647067]">
          Real board exam papers. Pick your subject, then view or download the
          original PDF.
        </p>

        {/* Board */}
        <div className="mt-8">
          <p className="text-xs font-extrabold uppercase tracking-[0.12em] text-[#647067]">Board</p>
          <div className="mt-2.5 flex flex-wrap gap-2">
            {["CBSE", "ICSE"].map((b) => (
              <button
                key={b}
                data-testid={`pyq-board-${b.toLowerCase()}`}
                onClick={() => setBoard(b)}
                className={pill(board === b)}
              >
                {b}
              </button>
            ))}
          </div>
        </div>

        {/* Class */}
        <div className="mt-6">
          <p className="text-xs font-extrabold uppercase tracking-[0.12em] text-[#647067]">Class</p>
          <div className="mt-2.5 flex flex-wrap gap-2">
            {["10", "12"].map((k) => (
              <button
                key={k}
                data-testid={`pyq-class-${k}`}
                onClick={() => setKlass(k)}
                className={pill(klass === k)}
              >
                Class {k}
              </button>
            ))}
          </div>
        </div>

        {/* Subject */}
        <div className="mt-6">
          <p className="text-xs font-extrabold uppercase tracking-[0.12em] text-[#647067]">Subject</p>
          <div className="mt-2.5 flex flex-wrap gap-2">
            {subjects.map((s, i) => (
              <button
                key={s}
                data-testid={`pyq-subject-${i}`}
                onClick={() => setSubject(s)}
                className={pill(subject === s)}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Filters — year only; Regular/Compartment papers appear together */}
        {papers && papers.length > 0 && years.length > 1 && (
          <div className="mt-8 flex flex-wrap items-center gap-2">
            <button
              data-testid="pyq-year-all"
              onClick={() => setYearFilter("all")}
              className={pill(yearFilter === "all")}
            >
              All years
            </button>
            {years.map((y) => (
              <button
                key={y}
                data-testid={`pyq-year-${y}`}
                onClick={() => setYearFilter(String(y))}
                className={pill(yearFilter === String(y))}
              >
                {y}
              </button>
            ))}
          </div>
        )}

        {/* Papers */}
        <div className="mt-8" data-testid="pyq-list">
          {papers === null ? (
            <div className="flex justify-center pt-16" data-testid="pyq-loading">
              <Loader2 className="h-7 w-7 animate-spin text-[#16A34A]" />
            </div>
          ) : shown.length === 0 ? (
            <div
              data-testid="pyq-empty"
              className="rounded-3xl border border-dashed border-[#16A34A]/30 bg-white p-10 text-center"
            >
              <FileText className="mx-auto h-9 w-9 text-[#16A34A]/50" />
              <p className="mt-4 font-display text-lg font-extrabold text-[#17201A]">
                Not available yet
              </p>
              <p className="mx-auto mt-1.5 max-w-sm text-sm font-semibold text-[#647067]">
                {papers.length > 0
                  ? "No papers match this year filter. Try another year."
                  : `We don't have ${board} Class ${klass} ${subject} papers yet — new papers are added regularly.`}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {shown.map((p) => (
                <div
                  key={p.id}
                  data-testid={`pyq-paper-${p.id}`}
                  className="flex flex-col gap-4 rounded-2xl border border-[#E3ECE6] bg-white p-5 sm:flex-row sm:items-center"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#16A34A]/15 bg-[#16A34A]/8 text-[#16A34A]">
                    <FileText className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-display text-base font-extrabold text-[#17201A]">
                      {p.displayName ||
                        `${p.subject} ${p.year || ""}${p.setCode ? ` · ${p.setCode}` : ""}`}
                    </p>
                    <p className="mt-0.5 truncate text-xs font-semibold text-[#647067]">
                      {p.board} · Class {p.klass} · {p.examType}
                      {p.sizeBytes ? ` · ${fmtSize(p.sizeBytes)}` : ""}
                    </p>
                    <p className="mt-0.5 truncate text-[11px] font-semibold text-[#94A39B]">
                      {p.filename}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <button
                      data-testid={`pyq-view-${p.id}`}
                      onClick={() => openViewer(p)}
                      disabled={viewerLoading}
                      className="inline-flex items-center gap-1.5 rounded-full bg-[#16A34A] px-4 py-2 text-xs font-bold text-white transition-colors duration-200 hover:bg-[#15803D] disabled:opacity-50"
                    >
                      <Eye className="h-3.5 w-3.5" />
                      View PDF
                    </button>
                    <button
                      data-testid={`pyq-download-${p.id}`}
                      onClick={() => download(p)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-[#16A34A]/30 px-4 py-2 text-xs font-bold text-[#16A34A] transition-colors duration-200 hover:bg-[#16A34A] hover:text-white"
                    >
                      <Download className="h-3.5 w-3.5" />
                      Download
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* PDF viewer modal */}
      {viewer && (
        <div
          data-testid="pyq-viewer-modal"
          className="fixed inset-0 z-50 flex flex-col bg-[#17201A]/60 p-3 backdrop-blur-sm sm:p-6"
        >
          <div className="mx-auto flex w-full max-w-5xl flex-1 flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between gap-3 border-b border-[#E3ECE6] px-5 py-3">
              <p className="truncate text-sm font-bold text-[#17201A]">{viewer.name}</p>
              <div className="flex shrink-0 items-center gap-2">
                {viewer.touch && (
                  <button
                    data-testid="pyq-viewer-download"
                    onClick={() => download(viewer.paper)}
                    aria-label="Download PDF"
                    title="Download PDF"
                    className="flex h-8 w-8 items-center justify-center rounded-full border border-[#E3ECE6] text-[#16A34A] transition-colors duration-200 hover:border-[#16A34A]"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                )}
                <button
                  data-testid="pyq-viewer-close"
                  onClick={closeViewer}
                  aria-label="Close viewer"
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#E3ECE6] text-[#17201A] transition-colors duration-200 hover:border-[#16A34A]"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            {viewer.touch ? (
              <PdfViewer blob={viewer.blob} />
            ) : (
              <iframe
                data-testid="pyq-viewer-frame"
                title={viewer.name}
                src={viewer.url}
                className="w-full flex-1"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PyqLibrary;
